'use client';

import Image from 'next/image';
import { type ReactNode, useEffect, useState } from 'react';
import {
  getThemeBackgroundClass,
  getThemeBadgeBorderClass,
  getThemeBorderClass,
  getThemeTextClass,
} from '../lib/theme';
import { TCustomIcon, THeroIcon } from '../types';
import { ITheme } from '../types/theme';
import CtaButtons from './CtaButtons';
import ChatGPTBadge from './ChatGPTBadge';
import Icon from './Icon';
import TitleBarSub from './TitleBarSub';
import WaveDivider from './WaveDivider';
import { ctaClick } from '@/lib/analytics';

export interface HeroEnhancedProps {
  title: string;
  subtitle: string;
  heroPoints?: ReactNode[];
  image: string;
  imageAlt: string;
  categoryBadge?: string;
  theme?: ITheme;
  language: 'en' | 'zh';
  showAppBadges?: boolean;
  includeDemo?: boolean;
  icon?: THeroIcon | TCustomIcon;
  imageSlideshow?: string[];
  heroMedia?: ReactNode;
  /** Adds a "Full page demo" button that opens this URL in a new window. */
  fullPageDemoUrl?: string;
}

export default function HeroEnhanced({
  title,
  subtitle,
  heroPoints,
  image,
  imageAlt,
  categoryBadge,
  theme = null,
  language,
  showAppBadges = true,
  includeDemo = true,
  icon,
  imageSlideshow,
  heroMedia,
  fullPageDemoUrl,
}: HeroEnhancedProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Slideshow effect
  useEffect(() => {
    if (!imageSlideshow || imageSlideshow.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % imageSlideshow.length);
    }, 6000); // Change slide every 6 seconds

    return () => clearInterval(interval);
  }, [imageSlideshow]);

  const gradientClass = getThemeBackgroundClass(theme, 'secondary');

  const textColorClass = getThemeTextClass(theme);

  const badgeBorderClass = getThemeBadgeBorderClass(theme);
  const borderClass = getThemeBorderClass(theme);

  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-br ${gradientClass}`}
    >
      {/* Breadcrumbs - positioned at absolute top */}
      <div className="absolute top-0 left-0 right-0 z-10 pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TitleBarSub language={language} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        {/* Wrapper for content and slideshow - slideshow positioned relative to this */}
        <div className="relative">
          {/* Desktop Slideshow - positioned absolutely on right side */}
          {imageSlideshow && imageSlideshow.length > 0 && (
            <div
              className="hidden lg:block absolute top-0 bottom-0 w-[55%]"
              style={{ right: 'calc(-1 * (100vw - 100%) / 2)' }}
            >
              {/* Background slideshow images */}
              {imageSlideshow.map((slideshowImage, index) => (
                <Image
                  key={slideshowImage}
                  src={slideshowImage}
                  alt={`${imageAlt} ${index + 1}`}
                  fill
                  sizes="(min-width: 1024px) 55vw, 100vw"
                  className={`object-cover object-center transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                  priority={index === 0}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              ))}
              {/* Foreground image overlay */}
              <div className="absolute bottom-0 right-0 w-[85%]">
                <Image
                  src={image}
                  alt={imageAlt}
                  width={700}
                  height={600}
                  className="w-full h-auto"
                  priority
                />
              </div>
              {/* Left fade gradient overlay - scattered/feathered edge */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `
                    linear-gradient(to right, var(--color-background-secondary) 0%, transparent 25%),
                    linear-gradient(115deg, var(--color-background-secondary) 0%, transparent 20%),
                    linear-gradient(95deg, var(--color-background-secondary) 0%, transparent 22%)
                  `,
                }}
              />
            </div>
          )}

          <div
            className={`relative z-10 grid ${imageSlideshow && imageSlideshow.length > 0 ? 'lg:grid-cols-1 lg:max-w-[50%]' : 'lg:grid-cols-[3fr_2fr] gap-12 lg:gap-16'} items-center`}
          >
            {/* Left Content */}
            <div className="space-y-8">
              {/* Title */}
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight hero-title flex items-center gap-4">
                <span>{title}</span>
              </h1>

              {/* Subtitle */}
              <p
                className="text-xl lg:text-2xl leading-relaxed font-semibold"
                style={{ color: 'var(--color-foreground-secondary)' }}
              >
                {subtitle}
              </p>

              {/* Key Points */}
              {heroPoints && heroPoints.length > 0 && (
                <ul className="space-y-4">
                  {heroPoints.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span
                        className="font-semibold"
                        style={{ color: 'var(--color-foreground)' }}
                      >
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/* CTA */}
              <CtaButtons
                theme={theme}
                includeDemo={includeDemo}
                language={language}
                fullPageDemoUrl={fullPageDemoUrl}
              />

              {/* App Store Badges */}
              {showAppBadges && (
                <div className="flex flex-wrap gap-4 items-center justify-center sm:justify-start">
                  <a
                    href="https://apps.apple.com/app/id1484789450"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      ctaClick(
                        'app_store',
                        'hero',
                        language,
                        'https://apps.apple.com/app/id1484789450'
                      )
                    }
                    className="transition-transform hover:scale-105"
                  >
                    <Image
                      src="/assets/icons/custom/custom-app-store.svg"
                      alt={
                        language === 'en'
                          ? 'Download on the App Store'
                          : '在 App Store 下載'
                      }
                      width={137}
                      height={40}
                      style={{ backgroundColor: 'white', borderRadius: '6px' }}
                      className="h-10 w-auto"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.omceanbooking.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      ctaClick(
                        'google_play',
                        'hero',
                        language,
                        'https://play.google.com/store/apps/details?id=com.omceanbooking.app'
                      )
                    }
                    className="transition-transform hover:scale-105"
                  >
                    <Image
                      src="/assets/icons/custom/custom-google-play.svg"
                      alt={
                        language === 'en'
                          ? 'Get it on Google Play'
                          : '在 Google Play 下載'
                      }
                      width={137}
                      height={40}
                      style={{ backgroundColor: 'white', borderRadius: '6px' }}
                      className="h-10 w-auto"
                    />
                  </a>
                  <ChatGPTBadge language={language} />
                </div>
              )}
            </div>

            {/* Right Image or Icon - only show when NOT using slideshow */}
            {!(imageSlideshow && imageSlideshow.length > 0) && (
              <div
                className={`relative flex justify-center ${heroMedia || image ? 'lg:justify-end' : 'hidden lg:flex lg:justify-center'} items-center overflow-visible`}
              >
                {heroMedia ? (
                  <div className="w-full animate-[slide-in-right_0.8s_ease-out]">
                    {heroMedia}
                  </div>
                ) : image ? (
                  // Render single Image (prioritize image over icon)
                  <div className="relative transform rotate-3 transition-transform hover:rotate-0 duration-300 animate-[slide-in-right_0.8s_ease-out]">
                    <div className="border-8 border-white dark:border-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                      <Image
                        src={image}
                        alt={imageAlt}
                        width={500}
                        height={500}
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                ) : icon ? (
                  // Render Icon with themed border (fallback if no image)
                  <div
                    className={`relative w-64 h-64 flex items-center justify-center rounded-full border-2 ${borderClass} bg-white dark:bg-gray-800 shadow-2xl p-16`}
                  >
                    <Icon
                      icon={icon}
                      className="w-full h-full text-gray-900 dark:text-white"
                    />
                  </div>
                ) : null}
                {/* Category Badge on Image */}
                {categoryBadge && (
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 z-10">
                    <div
                      className={`px-6 py-3 rounded-full bg-white dark:bg-gray-800 shadow-xl border-4 ${badgeBorderClass}`}
                    >
                      <span className={`text-sm font-bold ${textColorClass}`}>
                        {categoryBadge}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Slideshow - shown below content when stacked */}
          {imageSlideshow && imageSlideshow.length > 0 && (
            <div className="lg:hidden relative mt-8 flex justify-center">
              <div className="relative w-full max-w-sm overflow-hidden rounded-xl">
                {/* Background slideshow images */}
                {imageSlideshow.map((slideshowImage, index) => (
                  <Image
                    key={`mobile-${slideshowImage}`}
                    src={slideshowImage}
                    alt={`${imageAlt} ${index + 1}`}
                    width={400}
                    height={300}
                    className={`w-full h-auto object-cover transition-opacity duration-1000 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    } ${index === 0 ? 'relative' : 'absolute inset-0'}`}
                    priority={index === 0}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                ))}
                {/* Foreground image overlay - positioned to right */}
                <div className="absolute bottom-0 right-0 w-[85%]">
                  <Image
                    src={image}
                    alt={imageAlt}
                    width={350}
                    height={300}
                    className="w-full h-auto"
                    priority
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <WaveDivider
        position="bottom"
        backgroundColor="secondary"
        theme={theme}
      />
    </section>
  );
}
